import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge } from "@learn-me/ui";
import { categoriesApi } from "@/features/categories/api/categoriesApi";
import { Plus, Edit, Trash2, Layers, Star, GripVertical } from "lucide-react";
import toast from "react-hot-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { handleApiError } from "@/lib/errorHandler";
import { CategoryModal } from "@/features/admin/components/CategoryModal";
import type { Category } from "@learn-me/types";

export default function CategoriesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    category: Category | null;
  }>({
    isOpen: false,
    category: null,
  });
  const queryClient = useQueryClient();

  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: () => categoriesApi.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categoría eliminada");
      setDeleteDialog({ isOpen: false, category: null });
    },
    onError: (error) => {
      handleApiError(error, "Error al eliminar la categoría");
    },
  });

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (category: Category) => {
    setDeleteDialog({ isOpen: true, category });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.category) {
      deleteMutation.mutate(deleteDialog.category.id);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const mainAreas = categories?.filter((c) => c.isMainArea) || [];
  const regularCategories = categories?.filter((c) => !c.isMainArea) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Categorías</h1>
          <p className="text-muted-foreground">
            Gestiona las áreas de formación y categorías de cursos.
          </p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nueva Categoría
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-6 bg-muted rounded w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          {/* Áreas de Formación Principales */}
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Star className="h-5 w-5 text-gold-500" />
              Áreas de Formación Principales
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Las 3 áreas principales que se muestran en la página de inicio.
            </p>
            {mainAreas.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">
                    No hay áreas principales definidas.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {mainAreas.map((category) => (
                  <Card
                    key={category.id}
                    className="border-gold-200 bg-gold-50/30"
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{category.name}</h3>
                          <Badge variant="default" className="bg-gold-500">
                            Área Principal #{category.position}
                          </Badge>
                        </div>
                        {category.description && (
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {category.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {category.coursesCount || 0} cursos
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(category)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteClick(category)}
                          disabled={
                            deleteMutation.isPending ||
                            (category.coursesCount ?? 0) > 0
                          }
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Otras Categorías */}
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Otras Categorías
            </h2>
            {regularCategories.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">
                    No hay otras categorías.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {regularCategories.map((category) => (
                  <Card key={category.id}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{category.name}</h3>
                        </div>
                        {category.description && (
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {category.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {category.coursesCount || 0} cursos
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(category)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteClick(category)}
                          disabled={
                            deleteMutation.isPending ||
                            (category.coursesCount ?? 0) > 0
                          }
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal para crear/editar */}
      <CategoryModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        category={editingCategory}
        mainAreasCount={mainAreas.length}
      />

      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, category: null })}
        onConfirm={handleConfirmDelete}
        title="Eliminar Categoría"
        description={`¿Estás seguro de eliminar "${deleteDialog.category?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
